/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback } from 'react';
import { SendEmailNotificationActionType, SendEmailNotificationErrorType, SendEmailNotificationInputDataType } from '../types';
import { getIdentities, searchIdentities } from 'src/azure/services/get-board-users';
import { arrayIsEmpty, stringRequired } from '../../utils/validations';
import { IIdentity } from 'azure-devops-extension-api/Identities';

export const initialSendEmailNotification: SendEmailNotificationActionType = {
	to: [],
	subject: '',
	text: ''
};

export default function useSendEmailNotification() {
	const searchIdentityOptions = useCallback(async (text: string) => {
		const results = await searchIdentities(text, { withGroup: true });
  
		return results.map((item: any) => ({
			label: `${item.displayName} <${item.mail}>`,
			value: item.localId
		}));
	}, []);

	const getIdentityOptions = useCallback(async (includeIds?: string[]) => {
		const identities = await getIdentities();
		const options: Array<{label: string; value: string}> = identities!.map((item: any) => ({
			label: `${item.displayName} <${item.mail}>`,
			value: item.localId
		}));

		if (includeIds) {
			const identities = await getIdentitiesByIds(includeIds);
			identities.forEach(item => {
				if (!options.some(optItem => optItem.value === item.value)) {
					options.push(item);
				}
			});
		}

		return options;
	}, []);

	const getIdentitiesByIds = useCallback(async (ids: Array<string>) => {
		const results = await Promise.all(ids.map(async (id) => {
			const identities = await searchIdentities(id, { withGroup: true, queryTypeHint: 'uid' });
			const found: any = identities.find((identity) => (identity as IIdentity & { localId: string }).localId === id);
			return {
				label: `${found?.displayName} <${found?.mail}>`,
				value: found?.localId
			};
		}));

		return results;
	}, []);

	const validate = useCallback((values: {
		to: Array<string>;
		subject: string;
		text: string;
	}): {
		hasErrors: () => boolean;
		errors: SendEmailNotificationErrorType;
	} => {
		let hasErrors = false;
		const validations = {
			selectedOptions: [
				arrayIsEmpty(values.to, 'This field is required')
			],
			subject: [
				stringRequired(values.subject, 'This field is required')
			],
			text: [
				stringRequired(values.text, 'This field is required')
			]
		};

		const getError = (validation: Array<string | undefined>) => {
			const foundError = validation.find(item => item !== undefined) || null;
			if (foundError) {
				hasErrors = true;
			}
			
			return foundError || null;
		};

		return {
			hasErrors: () => hasErrors,
			errors: {
				to: getError(validations.selectedOptions),
				subject: getError(validations.subject),
				text: getError(validations.text)
			}
		};
	}, []);

	const getValues = useCallback((data: SendEmailNotificationActionType) => {
		return {
			to: data.to,
			subject: data.subject,
			text: data.text,
		};
	}, []);

	const loadValues = useCallback(async (data: SendEmailNotificationInputDataType): Promise<SendEmailNotificationActionType> => {
		const options = await getIdentityOptions();
		const identities = await getIdentitiesByIds(data.to);

		identities.forEach(item => {
			if (!options.some(optItem => optItem.value === item.value)) {
				options.push(item);
			}
		});

		return {
			to: identities.map(item => item.value),
			subject: data.subject,
			text: data.text,
		};
	}, []);

	return {
		searchIdentityOptions,
		getIdentityOptions,
		validate,
		getValues,
		loadValues
	};
}